import { useCallback, useMemo, useState } from 'react';
import { Edge } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { ListResponse as OverridesListResponse } from '@weave/schema-gen-ts/dist/schemas/phone/override/v2/override_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useAlert } from '@frontend/design-system';
import {
  findAllPhoneTreeAncestors,
  getDialOptionChildren,
  getRootNode,
  isAssociativeNode,
} from '../../helpers/graph/node-utils';
import {
  getNodesForBreakHoursEdit,
  getNodesForDelete,
  getNodesForGraphDuplication,
  getNodesForNonAssociativeAdd,
  getNodesForNonAssociativeEdit,
  getPhoneHoursNodesForAdd,
  getPhoneHoursNodesForEdit,
  getPhoneTreeNodesForAdd,
  getPhoneTreeNodesForEdit,
  getWeaveAssistantNodesForAdd,
  getWeaveAssistantNodesForEdit,
} from '../../helpers/graph/nodes-get-operations';
import {
  shouldShowPhoneTreeOverrideBlocker,
  shouldShowPhoneTreeOverrideBlockerForDuplicate,
  shouldShowWarning,
  shouldShowWarningForDuplicate,
} from '../../helpers/graph/warning-validators';
import { logError } from '../../helpers/utils';
import { NodeAction, NodeActionType } from '../../types';
import { PhoneTreeAncestors } from '../add-step-panel/repeat-step';
import { isNodeStateOfType, NodeStateWithAdditionalData } from '../add-step-panel/types';

export interface UseNodeActionsParams {
  callRouteId: string;
  getNodes: () => CallRouteV1.Types.Node[];
  getEdges: () => Edge[];
  updateEditingState: (nodes: CallRouteV1.Types.Node[], edges: Edge[]) => void;
  setContextNodeId: (nodeId: string | undefined) => void;
  callRouteEdges: Edge[] | undefined;
  tempEditingEdges: Edge[] | undefined;
  extendedNodes: Array<CallRouteV1.Types.Node & { isPhoneTreeDescendent?: boolean }>;
  openConfirmationModal: () => void;
  closeAddStepModal: () => void;
  closeEditBreakModal: () => void;
  overrides: OverridesListResponse;
  openPhoneTreeOverrideUsageModal: () => void;
}

/**
 * Hook to manage node actions (add, edit, delete, duplicate).
 * Handles all node manipulation logic for the call route graph.
 */
export const useNodeActions = ({
  callRouteId,
  getNodes,
  getEdges,
  updateEditingState,
  setContextNodeId,
  callRouteEdges,
  tempEditingEdges,
  extendedNodes,
  openConfirmationModal,
  closeAddStepModal,
  closeEditBreakModal,
  overrides,
  openPhoneTreeOverrideUsageModal,
}: UseNodeActionsParams) => {
  const alert = useAlert();

  // PROCESSING STATE FOR NODE ACTIONS
  //
  // processingNodeId is the id of the node that the user is currently acting upon (adding, editing, deleting).
  // NOTE: when a user is adding a new step, the id will be the id of the node that the user wants to add a step to
  // (the node that is being extended which will ultimately be the parent of the new step). When a user is editing or
  // deleting a step, the id will be the id of the node that the user wants to edit or delete.
  const [processingNodeId, setProcessingNodeId] = useState<string | undefined>(undefined);
  const [processingActionType, setProcessingActionType] = useState<NodeActionType | undefined>(undefined);
  // tempProcessingData is the data to use when editing or adding a new step to the call route flow. We need to
  // temporarily store the data to use it after the user has confirmed the action in a modal
  const [tempProcessingData, setTempProcessingData] = useState<NodeStateWithAdditionalData | undefined>(undefined);
  // tempDuplicateData is the duplicate data to use when duplicating a phone tree subgraph. We need to temporarily
  // store the data to use it after the user has confirmed the action in a modal
  const [tempDuplicateData, setTempDuplicateData] = useState<
    { nodes: CallRouteV1.Types.Node[]; edges: Edge[]; rootNode: CallRouteV1.Types.Node } | undefined
  >(undefined);

  // The node that the user is currently acting upon (adding, editing, deleting).
  const processingNode = useMemo(() => {
    if (!processingNodeId) {
      return undefined;
    }
    return extendedNodes.find((node) => node.id === processingNodeId);
  }, [processingNodeId, extendedNodes]);

  // Clear processing state
  const clearProcessingState = useCallback(() => {
    setProcessingNodeId(undefined);
    setProcessingActionType(undefined);
    setTempProcessingData(undefined);
    setTempDuplicateData(undefined);
  }, []);

  // Handle adding a step
  const handleAddStep = useCallback(
    (nodeStateWithAdditionalData: NodeStateWithAdditionalData) => {
      const { nodeState, additionalData } = nodeStateWithAdditionalData;

      if (!nodeState.type || !nodeState.callObject || !processingNodeId || !processingActionType) {
        alert.error('Missing data for processing node: PC-a0b1c2d');
        logError('Missing data for processing node: PC-a0b1c2d', 'useNodeActions:handleAddStep', {
          nodeState,
          additionalData,
          processingNodeId,
          processingActionType,
          callRouteId,
        });
        return;
      }

      const nodesToUse = getNodes();
      const edgesToUse = getEdges();

      let data;
      const newTypeIsAssociative = isAssociativeNode(nodeState.type);
      if (newTypeIsAssociative) {
        if (isNodeStateOfType(nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE)) {
          data = getPhoneTreeNodesForAdd({
            nodeState,
            additionalData,
            extendingNodeId: processingNodeId ?? '',
            nodes: nodesToUse,
            edges: edgesToUse,
          });
        } else if (isNodeStateOfType(nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS)) {
          data = getPhoneHoursNodesForAdd({
            nodeState,
            extendingNodeId: processingNodeId ?? '',
            nodes: nodesToUse,
            edges: edgesToUse,
          });
        } else {
          const errorMessage = 'Unhandled associative node type: PC-i6j7k8l';
          alert.error(errorMessage);
          logError(errorMessage, 'useNodeActions:handleAddStep', {
            nodeState,
            processingNodeId,
            callRouteId,
          });
          return;
        }
      } else if (isNodeStateOfType(nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT)) {
        data = getWeaveAssistantNodesForAdd({
          nodeState,
          extendingNodeId: processingNodeId ?? '',
          nodes: nodesToUse,
          edges: edgesToUse,
        });
      } else {
        data = getNodesForNonAssociativeAdd({
          nodeState,
          processingNodeId: processingNodeId ?? '',
          nodes: nodesToUse,
          edges: edgesToUse,
        });
      }

      const { nodes: newNodes, edges: newEdges } = data;
      updateEditingState(newNodes, newEdges);
      closeAddStepModal();
    },
    [
      processingNodeId,
      processingActionType,
      callRouteId,
      getNodes,
      getEdges,
      updateEditingState,
      closeAddStepModal,
      alert,
    ]
  );

  const applyEditStep = useCallback(
    ({ nodeState, additionalData }: NodeStateWithAdditionalData, editingNodeId: string) => {
      if (!nodeState.type || !nodeState.callObject) {
        const errorMessage = 'Missing data for editing node: PC-k8l9m0n';
        alert.error(errorMessage);
        logError(errorMessage, 'useNodeActions:handleEditStep', {
          nodeState,
          additionalData,
          processingNodeId,
          editingNodeId,
          callRouteId,
        });
        return;
      }

      const nodesToUse = getNodes();
      const edgesToUse = getEdges();

      let data;
      const newTypeIsAssociative = isAssociativeNode(nodeState.type);
      if (newTypeIsAssociative) {
        if (isNodeStateOfType(nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE)) {
          data = getPhoneTreeNodesForEdit({
            nodeState,
            additionalData,
            editingNodeId,
            nodes: nodesToUse,
            edges: edgesToUse,
          });
        } else if (isNodeStateOfType(nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS)) {
          data = getPhoneHoursNodesForEdit({
            nodeState,
            editingNodeId,
            nodes: nodesToUse,
            edges: edgesToUse,
          });
        } else {
          const errorMessage = 'Unhandled associative node type: PC-s4t5u6v';
          alert.error(errorMessage);
          logError(errorMessage, 'useNodeActions:handleEditStep', {
            nodeState,
            processingNodeId,
            editingNodeId,
            callRouteId,
          });
          return;
        }
      } else if (isNodeStateOfType(nodeState, CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT)) {
        data = getWeaveAssistantNodesForEdit({
          nodeState,
          editingNodeId,
          nodes: nodesToUse,
          edges: edgesToUse,
        });
      } else {
        data = getNodesForNonAssociativeEdit({
          nodeState,
          processingNodeId: editingNodeId,
          nodes: nodesToUse,
          edges: edgesToUse,
        });
      }

      const { nodes: newNodes, edges: newEdges } = data;
      updateEditingState(newNodes, newEdges);
      closeAddStepModal();
    },
    [processingNodeId, callRouteId, getNodes, getEdges, updateEditingState, closeAddStepModal, alert]
  );

  // Handle editing a step. Optionally accepts a node id override for direct-save paths.
  const handleEditStep = useCallback(
    (nodeStateWithAdditionalData: NodeStateWithAdditionalData, editingNodeIdOverride?: string) => {
      applyEditStep(nodeStateWithAdditionalData, editingNodeIdOverride ?? processingNodeId ?? '');
    },
    [applyEditStep, processingNodeId]
  );

  // Handle deleting a step
  const handleDeleteStep = useCallback(
    (nodeId: string) => {
      const nodesToUse = getNodes();
      const edgesToUse = getEdges();
      // find the node that the user wants to delete
      const nodeToDelete = nodesToUse.find((node) => node.id === nodeId);
      if (!nodeToDelete) {
        const errorMessage = 'Could not find node to delete: PC-g5h6i7j';
        alert.error(errorMessage);
        logError(errorMessage, 'useNodeActions:handleDeleteStep', {
          nodeId,
          availableNodes: nodesToUse.map((n) => n.id),
          callRouteId,
        });
        return;
      }

      const { nodes: newNodes, edges: newEdges } = getNodesForDelete({
        nodeId,
        nodes: nodesToUse,
        edges: edgesToUse,
      });

      setTimeout(() => {
        // We need to set the nodes and edges in the store after the confirmation modal is closed
        // to avoid UI changing while the modal is still open.
        updateEditingState(newNodes, newEdges);
      }, 0);
    },
    [callRouteId, getNodes, getEdges, updateEditingState, alert]
  );

  // Execute duplicate subgraph
  const executeDuplicateSubGraph = useCallback(
    ({ duplicatedNodes, duplicatedEdges }: { duplicatedNodes: CallRouteV1.Types.Node[]; duplicatedEdges: Edge[] }) => {
      if (!processingNodeId) {
        return;
      }

      const nodesToUse = getNodes();
      const edgesToUse = getEdges();

      const isReplace = processingActionType === NodeAction.Edit;

      const { nodes: newNodes, edges: newEdges } = getNodesForGraphDuplication({
        extendingNodeId: processingNodeId,
        allNodes: nodesToUse,
        allEdges: edgesToUse,
        duplicatedNodes,
        duplicatedEdges,
        isReplace,
      });

      clearProcessingState();
      closeAddStepModal();

      setTimeout(() => {
        // We need to set the nodes and edges in the store after the add step modal is closed
        // to avoid UI changing while the modal is still open.
        updateEditingState(newNodes, newEdges);

        // Focus on the root node of the duplicated sub-graph (which should be the phone tree node that was duplicated).
        const rootNodeInDuplicated = getRootNode(duplicatedNodes, duplicatedEdges);
        setContextNodeId(rootNodeInDuplicated?.id || duplicatedNodes[0]?.id || undefined);
      }, 0);
    },
    [
      processingNodeId,
      processingActionType,
      getNodes,
      getEdges,
      updateEditingState,
      setContextNodeId,
      clearProcessingState,
      closeAddStepModal,
    ]
  );

  // Handle duplicate subgraph with warning check
  const handleDuplicateSubGraph = useCallback(
    ({ nodes: duplicatedNodes, edges: duplicatedEdges }: { nodes: CallRouteV1.Types.Node[]; edges: Edge[] }) => {
      if (!processingNodeId || !duplicatedNodes.length) {
        alert.error('Missing data for processing node: PC-a0b1c2d');
        logError('Missing data for processing node: PC-a0b1c2d', 'useNodeActions:handleDuplicateSubGraph', {
          processingNodeId,
          callRouteId,
        });
        return;
      }

      const nodesToUse = getNodes();
      const edgesToUse = getEdges();

      // Get the root node of the duplicated subgraph
      const rootNodeInDuplicated = getRootNode(duplicatedNodes, duplicatedEdges);
      if (!rootNodeInDuplicated) {
        alert.error('Could not find root node in duplicated subgraph: PC-b1c2d3e');
        logError(
          'Could not find root node in duplicated subgraph: PC-b1c2d3e',
          'useNodeActions:handleDuplicateSubGraph',
          {
            processingNodeId,
            callRouteId,
            duplicatedNodesCount: duplicatedNodes.length,
          }
        );
        return;
      }

      // Check if duplicating this phone tree would cause downstream nodes to be removed, and if any of those
      // downstream nodes are phone tree nodes used in overrides. If so, block the action and show the override
      // usage modal. This check must happen before the shouldShowWarningForDuplicate check since it is a hard block
      // (no confirmation option) while shouldShowWarningForDuplicate is a soft confirmation.
      if (
        shouldShowPhoneTreeOverrideBlockerForDuplicate({
          nodeId: processingNodeId,
          duplicateRootNode: rootNodeInDuplicated,
          nodes: nodesToUse,
          edges: edgesToUse,
          actionType: processingActionType ?? NodeAction.Add,
          overrides,
        })
      ) {
        openPhoneTreeOverrideUsageModal();
        return;
      }

      // Check if we need to show a warning to the user before proceeding with the duplication
      const showWarning = shouldShowWarningForDuplicate({
        nodeId: processingNodeId,
        duplicateRootNode: rootNodeInDuplicated,
        nodes: nodesToUse,
        edges: edgesToUse,
        actionType: processingActionType ?? NodeAction.Add,
      });

      if (showWarning) {
        // Save the duplicate data to use after the user has confirmed the action
        setTempDuplicateData({ nodes: duplicatedNodes, edges: duplicatedEdges, rootNode: rootNodeInDuplicated });
        openConfirmationModal();
        return;
      }

      // Proceed with the duplication
      executeDuplicateSubGraph({ duplicatedNodes, duplicatedEdges });
    },
    [
      processingNodeId,
      processingActionType,
      callRouteId,
      getNodes,
      getEdges,
      openConfirmationModal,
      openPhoneTreeOverrideUsageModal,
      overrides,
      executeDuplicateSubGraph,
      alert,
    ]
  );

  // Handle editing break hour node
  const handleEditBreakHourNode = useCallback(
    (newName: string) => {
      const { nodes: newNodes, edges: newEdges } = getNodesForBreakHoursEdit({
        processingNodeId: processingNodeId ?? '',
        nodes: getNodes(),
        edges: getEdges(),
        newName,
      });

      updateEditingState(newNodes, newEdges);
      clearProcessingState();
      closeEditBreakModal();
    },
    [processingNodeId, getNodes, getEdges, updateEditingState, clearProcessingState, closeEditBreakModal]
  );

  // Process step action with warning check
  // This is the main function to process adding or editing a step. This is the starting point for those actions
  // as it includes the warning check before proceeding.
  const processStepAction = useCallback(
    (nodeStateWithAdditionalData: NodeStateWithAdditionalData) => {
      const { nodeState } = nodeStateWithAdditionalData;

      if (!nodeState.type || !nodeState.callObject || !processingNodeId || !processingActionType) {
        const errorMessage = 'Missing data for processing node: PC-q2r3s4t';
        alert.error(errorMessage);
        logError(errorMessage, 'useNodeActions:processStepAction', {
          nodeState,
          processingNodeId,
          processingActionType,
          callRouteId,
        });
        return;
      }

      const nodesToUse = getNodes();
      const edgesToUse = getEdges();

      // Check if adding or editing this node would cause downstream nodes to be removed, and if any of those
      // downstream nodes are phone tree nodes used in overrides. If so, block the action and show the override
      // usage modal. This check must happen before the shouldShowWarning check since it is a hard block (no
      // confirmation option) while shouldShowWarning is a soft confirmation.
      if (
        shouldShowPhoneTreeOverrideBlocker({
          nodeId: processingNodeId,
          newNodeState: nodeState,
          nodes: nodesToUse,
          edges: edgesToUse,
          actionType: processingActionType,
          overrides,
        })
      ) {
        openPhoneTreeOverrideUsageModal();
        return;
      }

      // Check if we need to show a warning to the user before proceeding with the action. If so, save the data
      // to use after the user has confirmed the action and display the confirmation modal.
      const showWarning = shouldShowWarning({
        nodeId: processingNodeId,
        newNodeData: nodeStateWithAdditionalData,
        nodes: nodesToUse,
        edges: edgesToUse,
        actionType: processingActionType,
      });
      if (showWarning) {
        // Save the data to use after the user has confirmed the action and display the confirmation modal.
        setTempProcessingData(nodeStateWithAdditionalData);
        openConfirmationModal();
        return;
      }

      // Proceed with the action if no warning is needed
      if (processingActionType === NodeAction.Edit) {
        handleEditStep(nodeStateWithAdditionalData);
      } else if (processingActionType === NodeAction.Add) {
        handleAddStep(nodeStateWithAdditionalData);
      } else {
        const errorMessage = 'Unhandled action type: PC-y8z9a0b';
        alert.error(errorMessage);
        logError(errorMessage, 'useNodeActions:processStepAction', {
          processingActionType,
          processingNodeId,
          callRouteId,
        });
        return;
      }
    },
    [
      processingNodeId,
      processingActionType,
      callRouteId,
      getNodes,
      getEdges,
      openConfirmationModal,
      openPhoneTreeOverrideUsageModal,
      overrides,
      handleEditStep,
      handleAddStep,
      alert,
    ]
  );

  // Get editing node initial data
  // When editing a node, we need to get the initial data for that node to populate the edit form.
  const getEditingNodeInitialData = useCallback((): NodeStateWithAdditionalData | undefined => {
    if (!processingNodeId || !(processingActionType === NodeAction.Edit)) {
      return undefined;
    }

    const nodesToUse = getNodes();
    const editingNode = nodesToUse?.find((node) => node.id === processingNodeId);

    if (!editingNode) {
      const errorMessage = 'Could not find editing node: PC-g4h5i6j';
      alert.error(errorMessage);
      logError(errorMessage, 'useNodeActions:getEditingNodeInitialData', {
        processingNodeId,
        processingActionType,
        availableNodes: nodesToUse.map((n) => n.id),
        callRouteId,
      });
      return undefined;
    }

    let dialOptions: string[] = [];
    if (editingNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE) {
      // if this is a phone tree node, we need to get all of the dial options from the children nodes. We can
      // get the dial options from the nodes that have edges whose sourceId is equal to the editing node id and whose node
      // type is of type treeOption.
      const edgesToUse = tempEditingEdges ?? callRouteEdges ?? [];
      const targetNodeIds = edgesToUse.filter((edge) => edge.sourceId === editingNode.id).map((edge) => edge.targetId);
      const childNodes = nodesToUse.filter(
        (node) => targetNodeIds.includes(node.id) && node.type === CallRouteV1.Types.NodeType.NODE_TYPE_TREE_OPTION
      );
      dialOptions = childNodes.map((node) => node?.callObject?.primitiveName ?? '');
    }

    if (!editingNode.type) {
      const errorMessage = 'Editing node type is undefined: PC-k7l8m9n';
      alert.error(errorMessage);
      logError(errorMessage, 'useNodeActions:getEditingNodeInitialData', {
        processingNodeId,
        callRouteId,
      });
      return;
    }

    return {
      nodeState: {
        type: editingNode.type,
        callObject: editingNode.callObject,
      },
      additionalData: {
        dialOptions,
      },
    };
  }, [processingNodeId, processingActionType, callRouteId, getNodes, tempEditingEdges, callRouteEdges, alert]);

  // Gets all of the ancestors of the node that the user is currently editing that are phone tree nodes.
  // along with their children dial option nodes.
  const getAncestorPhoneTrees = useCallback(() => {
    if (!processingNodeId) {
      return {};
    }

    const nodesToUse = getNodes();
    const edgesToUse = getEdges();

    const ancestors = findAllPhoneTreeAncestors(processingNodeId, nodesToUse, edgesToUse);

    const phoneTreeAncestors = ancestors.reduce((acc, ancestor) => {
      const dialOptionNodes = getDialOptionChildren(ancestor.id, nodesToUse, edgesToUse);
      acc[ancestor.id] = {
        node: ancestor,
        dialOptionNodes,
      };
      return acc;
    }, {} as PhoneTreeAncestors);

    return phoneTreeAncestors;
  }, [processingNodeId, getNodes, getEdges]);

  // Confirmation handlers
  // These handlers are called after the user has confirmed the action in the confirmation modal.
  //
  const handleConfirmEditStep = useCallback(() => {
    if (!tempProcessingData || !tempProcessingData.nodeState.type || !tempProcessingData.nodeState.callObject) {
      const errorMessage = 'Missing type or callObject: PC-q3r4s5t';
      alert.error(errorMessage);
      logError(errorMessage, 'useNodeActions:handleConfirmEditStep', {
        tempProcessingData,
        processingNodeId,
        callRouteId,
      });
      return;
    }
    handleEditStep(tempProcessingData);
  }, [tempProcessingData, processingNodeId, callRouteId, handleEditStep, alert]);

  const handleConfirmAddStep = useCallback(() => {
    if (!tempProcessingData || !tempProcessingData.nodeState.type || !tempProcessingData.nodeState.callObject) {
      const errorMessage = 'Missing type or callObject: PC-u6v7w8x';
      alert.error(errorMessage);
      logError(errorMessage, 'useNodeActions:handleConfirmAddStep', {
        tempProcessingData,
        processingNodeId,
        callRouteId,
      });
      return;
    }
    handleAddStep(tempProcessingData);
  }, [tempProcessingData, processingNodeId, callRouteId, handleAddStep, alert]);

  const handleConfirmDeleteStep = useCallback(() => {
    const nodeId = processingNodeId;
    if (!nodeId) {
      const errorMessage = 'Could not find node to delete: PC-y9z0a1b';
      alert.error(errorMessage);
      logError(errorMessage, 'useNodeActions:handleConfirmDeleteStep', {
        processingNodeId,
        callRouteId,
      });
      return;
    }
    handleDeleteStep(nodeId);
  }, [processingNodeId, callRouteId, handleDeleteStep, alert]);

  const handleConfirmDuplicateStep = useCallback(() => {
    if (!tempDuplicateData || !tempDuplicateData.nodes.length) {
      const errorMessage = 'Missing duplicate data: PC-c2d3e4f';
      alert.error(errorMessage);
      logError(errorMessage, 'useNodeActions:handleConfirmDuplicateStep', {
        tempDuplicateData,
        processingNodeId,
        callRouteId,
      });
      return;
    }
    executeDuplicateSubGraph({
      duplicatedNodes: tempDuplicateData.nodes,
      duplicatedEdges: tempDuplicateData.edges,
    });
  }, [tempDuplicateData, processingNodeId, callRouteId, executeDuplicateSubGraph, alert]);

  return {
    // State
    processingNodeId,
    setProcessingNodeId,
    processingActionType,
    setProcessingActionType,
    tempProcessingData,
    processingNode,
    tempDuplicateData,

    // Actions
    handleAddStep,
    handleEditStep,
    handleDeleteStep,
    handleDuplicateSubGraph,
    handleEditBreakHourNode,
    processStepAction,
    clearProcessingState,

    // Helpers
    getEditingNodeInitialData,
    getAncestorPhoneTrees,

    // Confirmation handlers
    handleConfirmEditStep,
    handleConfirmAddStep,
    handleConfirmDeleteStep,
    handleConfirmDuplicateStep,
  };
};
